import { CheckboxRow, SelectField, TextField } from './Fields.js';
import { SliderField, formatCompact, formatMoney } from './SliderField.js';
import { SLIDER_LIMITS } from '../utils/rules.js';

export function TopRow({ form, scenario, update }) {
    const { options } = scenario;
    return (
        <div className="top_row">
            <div className="top_config">
                <SelectField
                    label="Citizenship"
                    primary
                    value={form.citizenship}
                    options={options.citizenship}
                    onChange={(v) => update('citizenship', v)}
                />
            </div>
            <div className="top_config">
                <SelectField
                    label="Income Type"
                    value={form.incomeType}
                    options={options.incomeType}
                    onChange={(v) => update('incomeType', v)}
                />
            </div>
            <div className="top_config">
                <SelectField
                    label="Occupancy"
                    value={form.occupancy}
                    options={options.occupancy}
                    disabled={scenario.disabled.occupancy}
                    onChange={(v) => update('occupancy', v)}
                />
            </div>
            <div className="top_config">
                <SelectField
                    label="Purpose"
                    value={form.purpose}
                    options={options.purpose}
                    onChange={(v) => update('purpose', v)}
                />
            </div>
        </div>
    );
}

export function LoanColumn({ form, scenario, update, limits }) {
    const { options, visible, disabled, limits: scenarioLimits } = scenario;

    return (
        <div className="content_config left">
            <div className="slider_note">Slide values or click to edit</div>

            <SliderField
                label="FICO"
                value={form.noFico ? 0 : form.fico}
                min={SLIDER_LIMITS.fico.min}
                max={SLIDER_LIMITS.fico.max}
                disabled={disabled.fico}
                onChange={(v) => update('fico', v)}
                limitMin={form.noFico ? null : (limits?.FicoMin ?? scenarioLimits.ficoFallback ?? null)}
            >
                <div className="checkbox_field checkbox_no_fico">
                    <CheckboxRow
                        label="No FICO"
                        checked={form.noFico}
                        onChange={(v) => update('noFico', v)}
                    />
                </div>
            </SliderField>

            <SliderField
                label="LTV"
                value={form.ltv}
                min={SLIDER_LIMITS.ltv.min}
                max={SLIDER_LIMITS.ltv.max}
                onChange={(v) => update('ltv', v)}
                limitMax={limits?.LTVMax ?? scenarioLimits.ltvFallback ?? null}
            />

            <SliderField
                label="Loan Amount"
                value={form.loanAmount}
                min={SLIDER_LIMITS.loanAmount.min}
                max={SLIDER_LIMITS.loanAmount.max}
                step={5000}
                onChange={(v) => update('loanAmount', v)}
                format={formatMoney}
                formatBound={formatCompact}
                limitMin={limits?.LoanAmountMin ?? scenarioLimits.loanAmountMinFallback ?? null}
                limitMax={limits?.LoanAmountMax ?? scenarioLimits.loanAmountMaxFallback ?? null}
            />

            <SelectField
                label="Lock Period"
                value={form.lockPeriod}
                options={options.lockPeriod}
                onChange={(v) => update('lockPeriod', v)}
            />
            <SelectField
                label="Amortization Type"
                value={form.amortizationType}
                options={options.amortizationType}
                disabled={disabled.amortizationType}
                onChange={(v) => update('amortizationType', v)}
            />
            <SelectField
                label="Bank Statement Months"
                value={form.bankStatementMonths}
                options={options.bankStatementMonths}
                hidden={!visible.bankStatementMonths}
                onChange={(v) => update('bankStatementMonths', v)}
            />
            <SelectField
                label="Closing as an Entity"
                value={form.closingEntity}
                options={options.closingEntity}
                hidden={!visible.closingEntity}
                onChange={(v) => update('closingEntity', v)}
            />

            <div className="col_field checkbox_field">
                <CheckboxRow
                    label="Interest Only"
                    checked={form.interestOnly}
                    disabled={disabled.interestOnly}
                    onChange={(v) => update('interestOnly', v)}
                />
                <CheckboxRow
                    label="Impound Waiver"
                    mark={visible.escrowWaiverNote ? '**' : undefined}
                    checked={form.escrowWaiver}
                    disabled={disabled.escrowWaiver}
                    onChange={(v) => update('escrowWaiver', v)}
                />
                <CheckboxRow
                    label="CDFI"
                    checked={form.cdfiEligible}
                    hidden={!visible.cdfi}
                    onChange={(v) => update('cdfiEligible', v)}
                />
                <CheckboxRow
                    label="Short-Term Rental"
                    checked={form.shortTermRental}
                    hidden={!visible.shortTermRental}
                    onChange={(v) => update('shortTermRental', v)}
                />
                <CheckboxRow
                    label="Living Rent Free"
                    checked={form.rentFree}
                    onChange={(v) => update('rentFree', v)}
                />
                <CheckboxRow
                    label="Asset Depletion"
                    mark="*"
                    checked={form.assetDepletion}
                    hidden={!visible.assetDepletion}
                    onChange={(v) => update('assetDepletion', v)}
                />
                <CheckboxRow
                    label="Texas 50(a)(6)"
                    checked={form.isTexas50A6}
                    disabled={disabled.isTexas50A6}
                    hidden={!visible.isTexas50A6}
                    onChange={(v) => update('isTexas50A6', v)}
                />
                <CheckboxRow
                    label="Lender Fee Buyout"
                    checked={form.lenderFeeBuyout}
                    hidden={!visible.lenderFeeBuyout}
                    onChange={(v) => update('lenderFeeBuyout', v)}
                />
                <CheckboxRow
                    label="ACH"
                    checked={form.ach}
                    hidden={!visible.ach}
                    onChange={(v) => update('ach', v)}
                />
            </div>

            <SelectField
                label="Pre-Asset Depletion DSCR"
                value={form.preAssetDepletion}
                options={options.preAssetDepletion}
                hidden={!visible.preAssetDepletion}
                onChange={(v) => update('preAssetDepletion', v)}
            />

            {visible.assetDepletion && (
                <p className="field_msg">
                    <span className="eva_error">*</span>
                    Asset Depletion is available to improve DSCR score (Pricing Adjustments Apply)
                </p>
            )}

            {visible.escrowWaiverNote && (
                <p className="field_msg">
                    <span className="eva_error">**</span>
                    Please see guidelines to determine when escrows are required
                </p>
            )}
        </div>
    );
}

export function PropertyColumn({ form, scenario, update }) {
    const { options, visible, disabled } = scenario;

    return (
        <div className="content_config right">
            <SelectField
                label="Property Type"
                value={form.propertyType}
                options={options.propertyType}
                onChange={(v) => update('propertyType', v)}
            />
            <SelectField
                label="Number of Units"
                value={form.units}
                options={options.units}
                disabled={form.propertyType !== 'MU'}
                onChange={(v) => update('units', v)}
            />
            <SelectField
                label="State"
                value={form.state}
                options={options.state}
                onChange={(v) => update('state', v)}
            />
            <SelectField
                label="Broker Compensation"
                value={form.brokerCompensation}
                options={options.brokerCompensation}
                disabled={disabled.brokerCompensation}
                hidden={!visible.brokerCompensation}
                onChange={(v) => update('brokerCompensation', v)}
            />
            <TextField
                label="Comp Plan %"
                value={form.compPlanRate}
                hidden={!visible.compPlan}
                onChange={(v) => update('compPlanRate', v)}
            />
            <SelectField
                label="DTI"
                value={form.dti}
                options={options.dti}
                disabled={disabled.dti}
                hidden={!visible.dti}
                onChange={(v) => update('dti', v)}
            />
            <SelectField
                label="YSP"
                value={form.ysp}
                options={options.ysp}
                hidden={!visible.ysp}
                onChange={(v) => update('ysp', v)}
            />
            <SelectField
                label="Prepayment Penalty"
                value={form.pppTerm}
                options={options.pppTerm}
                hidden={!visible.pppTerm}
                onChange={(v) => update('pppTerm', v)}
            />
            <SelectField
                label="Credit Event"
                value={form.housingEvent}
                options={options.housingEvent}
                onChange={(v) => update('housingEvent', v)}
            />
            <SelectField
                label="Mortgage History"
                value={form.creditSeasoning}
                options={options.creditSeasoning}
                onChange={(v) => update('creditSeasoning', v)}
            />
            <SelectField
                label="Lien Position"
                value={form.lienPosition}
                options={options.lienPosition}
                hidden={!visible.lienPosition}
                onChange={(v) => update('lienPosition', v)}
            />
        </div>
    );
}
